import type { Node } from "@xyflow/react";

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface VGuide {
  x: number;
  yMin: number;
  yMax: number;
}
export interface HGuide {
  y: number;
  xMin: number;
  xMax: number;
}

export interface SnapResult {
  dx: number;
  dy: number;
  vGuide: VGuide | null;
  hGuide: HGuide | null;
}

export function getAbsBox(node: Node, all: Node[]): Box {
  let px = 0;
  let py = 0;
  let cur: Node | undefined = node;
  while (cur?.parentId) {
    const parent: Node | undefined = all.find((n) => n.id === cur?.parentId);
    if (!parent) break;
    px += parent.position.x;
    py += parent.position.y;
    cur = parent;
  }
  const measured = (
    node as Node & { measured?: { width?: number; height?: number } }
  ).measured;
  const w = measured?.width ?? node.width ?? 150;
  const h = measured?.height ?? node.height ?? 50;
  return { x: node.position.x + px, y: node.position.y + py, w, h };
}

export function computeSnap(
  proposed: Box,
  candidates: Box[],
  threshold = 8,
): SnapResult {
  const verticals: ("left" | "center" | "right")[] = [
    "left",
    "center",
    "right",
  ];
  const horizontals: ("top" | "center" | "bottom")[] = [
    "top",
    "center",
    "bottom",
  ];
  const xLine = (b: Box, k: "left" | "center" | "right") =>
    k === "left" ? b.x : k === "right" ? b.x + b.w : b.x + b.w / 2;
  const yLine = (b: Box, k: "top" | "center" | "bottom") =>
    k === "top" ? b.y : k === "bottom" ? b.y + b.h : b.y + b.h / 2;

  let bestX: { diff: number; v: VGuide } | null = null;
  let bestY: { diff: number; h: HGuide } | null = null;

  for (const c of candidates) {
    for (const dk of verticals) {
      for (const ck of verticals) {
        const diff = xLine(c, ck) - xLine(proposed, dk);
        if (Math.abs(diff) > threshold) continue;
        if (bestX && Math.abs(diff) >= Math.abs(bestX.diff)) continue;
        bestX = {
          diff,
          v: {
            x: xLine(c, ck),
            yMin: Math.min(proposed.y, c.y),
            yMax: Math.max(proposed.y + proposed.h, c.y + c.h),
          },
        };
      }
    }
    for (const dk of horizontals) {
      for (const ck of horizontals) {
        const diff = yLine(c, ck) - yLine(proposed, dk);
        if (Math.abs(diff) > threshold) continue;
        if (bestY && Math.abs(diff) >= Math.abs(bestY.diff)) continue;
        bestY = {
          diff,
          h: {
            y: yLine(c, ck),
            xMin: Math.min(proposed.x, c.x),
            xMax: Math.max(proposed.x + proposed.w, c.x + c.w),
          },
        };
      }
    }
  }

  return {
    dx: bestX?.diff ?? 0,
    dy: bestY?.diff ?? 0,
    vGuide: bestX?.v ?? null,
    hGuide: bestY?.h ?? null,
  };
}
